import { FunctionComponent, useEffect, useState } from 'react'
import moment from 'moment-timezone'
import { ArrowBack, ArrowBackIosNew, ArrowForwardIos, Print, Restaurant } from '@mui/icons-material'
import styles from './draftingHandler.module.scss'
import { Daycare, DaycareType } from '@/types/Daycare'
import Title from '@/components/_custom/Title/title'
import Indicator from '@/components/_custom/Indicator/indicator'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper'
import Loading from '@/components/Loading/Loading'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import { HierarchicalOption } from '@/types/Select'
import { Section, SectionGroup } from '@/types/Section'
import { useAuth } from '@/modules/context/AuthContext'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { ChildsSlots, RateSlotsAndSlots, StaffSlots } from '@/types/StaffRate'
import { GET_RATE_SLOTS_AND_SLOTS } from '@/modules/API/staff/staffRate'
import TimeBar from '../TimeBar/timeBar'
import DraftingStaffSlots from './DraftingStaffSlots'
import DraftingChildSlots from './DraftingChildSlots'
import { OccasionalSlot, OccasionalSlotsPlaces } from '@/types/Slots'
import DraftingAvailableSlots from './DraftingAvailableSlots'
import Desktop from '@/modules/desktopMobile/desktop'
import { StaffWithLinks } from '@/types/Staff'
import ShowSlot from '../../planning/Dialog/showEvent'
import { ChildWithParents } from '@/types/Child'
import { EventCalendar } from '@/types/Calendar'
import { CalendarIcon } from '@mui/x-date-pickers'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import DatePickerDialog from '../../planning/Dialog/datePicker/datePickerDialog'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import EditOrCreateWorkDialog from '../../planning/Dialog/staffWork/editOrCreateStaffWork/editOrCreateWorkDialog'
import UserInput from '@/components/_custom/UserInput/userInput'
import { format, parse } from 'date-fns'
import SelectHierarchical from '@/components/_custom/Selector/selectHierarchical/selectHierarchical.tsx'
import ExportSectionSlotsDialog from './Dialog/exportSectionSlotsDialog.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDateNavigation } from '@/hooks/useDateNavigation'
import { Work } from '@/types/work'
import { getEffectiveDayBounds } from '../TimBarSlots/defineSlots'
import { computeAvailableHoursCount, computeChildCount } from './draftingCounts.ts'
import OccasionalSlotForm, {
    OccasionalSlotFormProps
} from '@/container/planning/Dialog/occasionalSlot/occasionalSlotForm/occasionalSlotForm.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog.tsx'

interface DraftingProps {
    currentDaycare: Daycare
    staffRateList: SectionGroup[]
    places: OccasionalSlotsPlaces[]
    childs: ChildWithParents[]
    staffsListData: StaffWithLinks[]
    defaultDate: string | null
    defaultSectionGroupId: string | null
}

const DraftingHandler: FunctionComponent<DraftingProps> = ({
    currentDaycare,
    staffRateList,
    places,
    childs,
    staffsListData,
    defaultDate,
    defaultSectionGroupId
}: DraftingProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationError } = useNotification()

    const navigate: NavigateFunction = useNavigate()

    const [searchChild, setSearchChild] = useState<string>('')
    const [searchChildsList, setSearchChildsList] = useState<ChildsSlots[]>([])

    function handleSearchChilds(childName: string): void {
        setSearchChild(childName)
        setSearchChildsList(
            rateSlotsAndSlots.childs.filter((child: ChildsSlots) =>
                child.childName.toLowerCase().includes(childName.toLowerCase())
            )
        )
    }

    const [getRateSlotsAndSlots, { loading: loadingSlots, refetch: refetchSlots, data: dataSlots, error: errorSlots }] =
        useLazyQuery(GET_RATE_SLOTS_AND_SLOTS, {
            fetchPolicy: 'network-only'
        })

    function handleDateChange(formatedDate: string, sectionIds: number[]): void {
        if (sectionIds.length > 0) {
            getRateSlotsAndSlots({
                variables: {
                    date: formatedDate,
                    daycareId: currentDaycare?.id,
                    sectionIds: sectionIds
                }
            })
        }
    }

    const {
        date,
        dayOpening,
        dayClosing,
        handleNextDate,
        handlePreviousDate,
        setDateValues,
        selectedSectionAndSectionGroup,
        setSelectedSectionAndSectionGroup,
        getSectionIdsFromSelection
    } = useDateNavigation({
        daycare: currentDaycare,
        defaultDate,
        onDateChange: handleDateChange
    })

    const [staffCount, setStaffCount] = useState<number>(0)
    const [childCount, setChildCount] = useState<number>(0)
    const [availableHoursCount, setAvailableHoursCount] = useState<number>(0)

    const [rateSlotsAndSlots, setRateSlotsAndSlots] = useState<RateSlotsAndSlots>({
        rate: [],
        availableSlots: [],
        childs: [],
        staffs: [],
        closingDay: false
    })

    const [isClosingDay, setIsClosingDay] = useState<boolean>(false)

    useEffect(() => {
        setStaffCount(0)
        setChildCount(0)
        setAvailableHoursCount(0)

        if (dataSlots) {
            setRateSlotsAndSlots(dataSlots.getRateSlotsAndSlots)
            setIsClosingDay(dataSlots.getRateSlotsAndSlots.closingDay || false)
            setStaffCount(dataSlots.getRateSlotsAndSlots.staffs.length)
            setSearchChildsList(dataSlots.getRateSlotsAndSlots.childs)
            setChildCount(computeChildCount(dataSlots.getRateSlotsAndSlots.childs))
            setAvailableHoursCount(
                computeAvailableHoursCount(dataSlots.getRateSlotsAndSlots.availableSlots, currentDaycare.merger)
            )
        }
    }, [dataSlots])

    useEffect(() => {
        if (errorSlots) {
            showNotificationError(t('slotsLoadedError'))
            setRateSlotsAndSlots({
                rate: [],
                availableSlots: [],
                childs: [],
                staffs: [],
                closingDay: false
            })
            setSearchChildsList([])
            setStaffCount(0)
            setChildCount(0)
            setAvailableHoursCount(0)
        }
    }, [errorSlots])

    async function refetchRateSlots(): Promise<void> {
        const { data } = await refetchSlots()

        if (data) {
            setRateSlotsAndSlots(data.getRateSlotsAndSlots)
            setStaffCount(data.getRateSlotsAndSlots.staffs.length)
            setChildCount(computeChildCount(data.getRateSlotsAndSlots.childs))
            setAvailableHoursCount(
                computeAvailableHoursCount(data.getRateSlotsAndSlots.availableSlots, currentDaycare.merger)
            )
        }
    }

    useEffect(() => {
        const selectedSectionIds: string[] = defaultSectionGroupId
            ? staffRateList
                  .filter((rl: SectionGroup): boolean => rl.id.toString() === defaultSectionGroupId)
                  .map((rl: SectionGroup): string[] =>
                      rl.sections.map((s: number): string => 'SECTION_' + s.toString())
                  )
                  .flat()
            : staffRateList.length > 0
              ? staffRateList[0].sections.map((s: number): string => 'SECTION_' + s.toString())
              : []

        setSelectedSectionAndSectionGroup([
            ...selectedSectionIds,
            defaultSectionGroupId
                ? defaultSectionGroupId
                : staffRateList.length > 0
                  ? staffRateList[0].id.toString()
                  : ''
        ])

        setStaffCount(0)
        setChildCount(0)
        setAvailableHoursCount(0)

        if (selectedSectionIds.length > 0) {
            getRateSlotsAndSlots({
                variables: {
                    date: date,
                    daycareId: currentDaycare?.id,
                    sectionIds: getSectionIdsFromSelection(selectedSectionIds)
                }
            })
        }
    }, [user?.currentDaycare, defaultSectionGroupId, staffRateList])

    function handleSelectSection(selected: string[]): void {
        setSelectedSectionAndSectionGroup(selected)
        const sectionIds: number[] = getSectionIdsFromSelection(selected)
        if (sectionIds.length > 0) {
            getRateSlotsAndSlots({
                variables: {
                    date: date,
                    daycareId: currentDaycare?.id,
                    sectionIds: sectionIds
                }
            })
        }
    }

    const selectHierarchicalOptions: HierarchicalOption[] = staffRateList.map(
        (sr: SectionGroup): HierarchicalOption => ({
            name: `${sr.name} - ${sr.rate} ${t('childsPerStaff')}`,
            value: sr.id.toString(),
            children: sr.sections.map((s: number): HierarchicalOption => {
                const section: Section | undefined =
                    currentDaycare.sections &&
                    currentDaycare.sections.find((section: Section): boolean => section.id === s)
                return { name: section?.name || '', value: 'SECTION_' + s.toString() }
            })
        })
    )

    function showMealUrl(): string {
        const sections: string | null =
            selectedSectionAndSectionGroup.length === 0
                ? null
                : selectedSectionAndSectionGroup
                      .filter((s: string): boolean => s.startsWith('SECTION_'))
                      .map((s: string): number => parseInt(s.replace('SECTION_', '')))
                      .join(',')

        return `/meal?date=${date}${sections ? '&sections=' + sections : ''}`
    }

    const [showSlotDialogOpen, setShowSlotDialogOpen] = useState<boolean>(false)
    const [selectedEvent, setSelectedEvent] = useState<EventCalendar | null>(null)

    const [addStaffDialogOpen, setAddStaffDialogOpen] = useState<boolean>(false)

    const [isDatePickerDialogOpen, setDatePickerDialogOpen] = useState(false)

    const [isOpenExport, setOpenExport] = useState<boolean>(false)

    const allWorksForDay: Work[] = rateSlotsAndSlots.staffs.flatMap((staff: StaffSlots) => staff.works)
    const { opening: effectiveOpening, closing: effectiveClosing } = getEffectiveDayBounds(
        allWorksForDay,
        dayOpening,
        dayClosing
    )

    const { dialog: addOccasionalSlotFormDialog, openDialog: openAddOccasionalSlotFormDialog } = useDialog({
        Component: OccasionalSlotForm,
        props: (onClose): OccasionalSlotFormProps => ({
            type: 'add',
            handleClose: onClose,
            refetchData: refetchRateSlots,
            childs,
            calendarEvents: [],
            places,
            selectedDate: date
        }),
        dialogTitle: t('editOccasionalSlot'),
        hideDialogClose: true
    })

    return staffRateList.length > 0 ? (
        <>
            <div className={styles.SelectorContainer}>
                {user && user.daycares.length > 1 && (
                    <div className={styles.SelectorLeft}>
                        <DaycareSelector />
                    </div>
                )}
                <div className={styles.SelectorRight}>
                    <div className={styles.SectionGroupContainer}>
                        {user && user.daycares.length > 1 && (
                            <ButtonIcon
                                icon={<Print />}
                                onClick={() => setOpenExport(true)}
                                className={styles.IconLeft}
                            />
                        )}
                        <div className={styles.SectionGroup}>
                            <SelectHierarchical
                                label={t('myStaffRate')}
                                options={selectHierarchicalOptions}
                                value={selectedSectionAndSectionGroup}
                                onChange={handleSelectSection}
                                fullWidth={true}
                            />
                        </div>
                        {user && user.daycares.length === 1 && (
                            <ButtonIcon
                                icon={<Print />}
                                onClick={() => setOpenExport(true)}
                                className={styles.IconRight}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.DraftingContainer}>
                <div className={styles.Header}>
                    <ArrowBackIosNew className={styles.ArrowIcon} onClick={handlePreviousDate} />
                    <div className={styles.TitleAndCalendar}>
                        <p className={styles.DateTitle}>
                            {format(parse(date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                locale: getLocalFromLanguage()
                            })}
                        </p>
                        <ButtonIcon
                            icon={<CalendarIcon />}
                            onClick={() => setDatePickerDialogOpen(true)}
                            className={styles.CalendarIcon}
                        />
                    </div>
                    <ArrowForwardIos className={styles.ArrowIcon} onClick={handleNextDate} />
                </div>
                {rateSlotsAndSlots && rateSlotsAndSlots.rate.length > 0 && (
                    <>
                        <TimeBar rateSlots={rateSlotsAndSlots.rate} opening={dayOpening} closing={dayClosing} />
                        {user?.currentDaycareRights.canReadMeal && childCount >= 1 && (
                            <div className={styles.MealButtonContainer}>
                                <ButtonTextIcon
                                    type={'custom'}
                                    onClick={() => navigate(showMealUrl())}
                                    customIcon={<Restaurant />}
                                    customText={t('showTodayMeal')}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            {loadingSlots && <Loading overall />}
            {isClosingDay ? (
                <div className={styles.DraftingContainer}>
                    <div className={styles.Text}>
                        <Title text={t('isClosingDay')} />
                    </div>
                </div>
            ) : (
                <>
                    {(childCount > 0 || staffCount > 0 || availableHoursCount > 0) && (
                        <div className={styles.IndicatorsContainer}>
                            <Indicator
                                count={staffCount}
                                backgroundWhite
                                text={staffCount > 1 ? t('staffs') : t('staff')}
                            />
                            <Indicator
                                count={childCount}
                                backgroundWhite
                                text={childCount > 1 ? t('childs') : t('child')}
                            />
                            <Desktop>
                                <Indicator
                                    count={availableHoursCount}
                                    backgroundWhite
                                    text={
                                        currentDaycare.merger
                                            ? availableHoursCount > 1
                                                ? t('availableHours')
                                                : t('availableHour')
                                            : availableHoursCount > 1
                                              ? t('availableSlots')
                                              : t('availableSlot')
                                    }
                                />
                            </Desktop>
                        </div>
                    )}
                    {rateSlotsAndSlots && (
                        <div className={styles.DraftingContainer}>
                            <div className={styles.Header}>
                                <Title text={t('staffs')} />
                                {user?.currentDaycareRights.canWriteStaffWorks && (
                                    <ButtonTextIcon type={'add'} onClick={() => setAddStaffDialogOpen(true)} />
                                )}
                            </div>
                            {rateSlotsAndSlots.staffs.length > 0 ? (
                                [...rateSlotsAndSlots.staffs]
                                    .sort((a: StaffSlots, b: StaffSlots) => {
                                        if (a.self !== b.self) return a.self ? -1 : 1
                                        return a.staffName.localeCompare(b.staffName)
                                    })
                                    .map((staffSlots: StaffSlots) => (
                                        <DraftingStaffSlots
                                            key={staffSlots.staffId}
                                            staffSlots={staffSlots}
                                            opening={effectiveOpening}
                                            closing={effectiveClosing}
                                            date={date}
                                            daycare={currentDaycare}
                                            staffsListData={staffsListData}
                                            allWorks={allWorksForDay}
                                            canWriteStaffWorks={!!user?.currentDaycareRights.canWriteStaffWorks}
                                            refetchData={refetchRateSlots}
                                            setSelectedEvent={setSelectedEvent}
                                            setShowSlotDialogOpen={setShowSlotDialogOpen}
                                        />
                                    ))
                            ) : (
                                <div className={styles.Text}>
                                    <p>{t('noStaffThisDay')}</p>
                                </div>
                            )}
                        </div>
                    )}
                    {rateSlotsAndSlots && rateSlotsAndSlots.childs.length > 0 && (
                        <div className={styles.DraftingContainer}>
                            <div className={styles.Header}>
                                <Title text={t('childsPresent')} />
                                <UserInput
                                    label={t('searchChild')}
                                    type={'text'}
                                    value={searchChild}
                                    setValue={handleSearchChilds}
                                />
                            </div>
                            {[...searchChildsList]
                                .sort((a: ChildsSlots, b: ChildsSlots) => a.childName.localeCompare(b.childName))
                                .map((childSlots: ChildsSlots) => (
                                    <DraftingChildSlots
                                        key={childSlots.childId}
                                        childSlots={childSlots}
                                        opening={dayOpening}
                                        closing={dayClosing}
                                        setSelectedEvent={setSelectedEvent}
                                        setShowSlotDialogOpen={setShowSlotDialogOpen}
                                        date={date}
                                    />
                                ))}
                        </div>
                    )}
                    {rateSlotsAndSlots && (
                        <div className={styles.DraftingContainer}>
                            <div className={styles.Header}>
                                <Title text={t('availableSlots')} />
                                {user?.currentDaycareRights.canWriteOccasionalSlot && (
                                    <ButtonTextIcon type={'add'} onClick={openAddOccasionalSlotFormDialog} />
                                )}
                            </div>
                            {rateSlotsAndSlots.availableSlots.length > 0 ? (
                                rateSlotsAndSlots.availableSlots.map((availableSlot: OccasionalSlot) => (
                                    <DraftingAvailableSlots
                                        key={availableSlot.id}
                                        availableSlot={availableSlot}
                                        opening={dayOpening}
                                        closing={dayClosing}
                                        setSelectedEvent={setSelectedEvent}
                                        setShowSlotDialogOpen={setShowSlotDialogOpen}
                                    />
                                ))
                            ) : (
                                <div className={styles.Text}>
                                    <p>{t('noAvailableSlotsThisDay')}</p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
            {addOccasionalSlotFormDialog}

            {user && showSlotDialogOpen && selectedEvent && (
                <ShowSlot
                    open={showSlotDialogOpen}
                    handleClose={() => setShowSlotDialogOpen(false)}
                    selectedEvent={selectedEvent}
                    daycare={currentDaycare}
                    refetchData={refetchRateSlots}
                    childs={childs}
                    staffsListData={staffsListData}
                    calendarEvents={[]}
                    places={places}
                    isParent={false}
                    works={allWorksForDay}
                    canEditRegular={user.currentDaycareRights.canWriteContracts}
                    canEditStaffWorks={user.currentDaycareRights.canWriteStaffWorks}
                    lastRegularSlotDate={moment().add(1, 'year').format('DD-MM-YYYY')}
                />
            )}
            {isDatePickerDialogOpen && (
                <DatePickerDialog
                    open={isDatePickerDialogOpen}
                    handleClose={() => setDatePickerDialogOpen(false)}
                    selectedDate={moment(date, 'DD-MM-YYYY')}
                    handleDateChange={setDateValues}
                    currentDaycare={currentDaycare}
                />
            )}
            {addStaffDialogOpen && (
                <EditOrCreateWorkDialog
                    open={addStaffDialogOpen}
                    handleClose={() => setAddStaffDialogOpen(false)}
                    works={allWorksForDay}
                    daycare={currentDaycare}
                    staffsListData={staffsListData}
                    date={date}
                    refetch={refetchRateSlots}
                    from={dayOpening}
                    to={dayClosing}
                />
            )}
            {isOpenExport && (
                <ExportSectionSlotsDialog
                    open={isOpenExport}
                    handleClose={() => setOpenExport(false)}
                    minDate={
                        currentDaycare.created
                            ? moment(currentDaycare.created, 'DD-MM-YYYY').format('DD-MM-YYYY')
                            : moment().format('DD-MM-YYYY')
                    }
                    daycare={currentDaycare}
                    startDate={date}
                    endDate={date}
                    preSelectedSections={selectedSectionAndSectionGroup
                        .filter((s: string): boolean => s.startsWith('SECTION_'))
                        .map((s: string): string => s.replace('SECTION_', ''))}
                    isRpeOrLaep={currentDaycare.type === DaycareType.rpe || currentDaycare.type === DaycareType.laep}
                />
            )}
            <ButtonTextIcon
                type={'custom'}
                onClick={() => navigate('/calendar?tab=global')}
                customText={t('backToPlanning')}
                customIcon={<ArrowBack />}
            />
        </>
    ) : (
        <>
            {user && user.daycares.length > 1 && (
                <div className={styles.SelectorContainer}>
                    <div className={styles.Selector}>
                        <DaycareSelector />
                    </div>
                </div>
            )}
            <div className={styles.DraftingContainer}>
                <div className={styles.Text}>
                    <Title text={t('forUseGlobalPlanning')} />
                    <p>
                        {t('youCanAddStaffRate')}
                        <span
                            className={styles.Link}
                            onClick={() => navigate(`/daycare?daycare=${user?.currentDaycare?.id}#staffSettings`)}
                        >
                            {t('clickHere')}
                        </span>
                    </p>
                </div>
            </div>
        </>
    )
}

export default DraftingHandler
